import PropTypes from 'prop-types';

import {Box, Flex, Skeleton, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {RichTextContent} from '@amblique/contentful/app/components/rich-text-content';
import {Component as ContentfulComponent} from '@amblique/king-living/app/components/contentful/component';

function filterValidItems(items) {
  return (items ?? []).filter(
    (item) => item != null && typeof item === 'object' && item.__typename && item.sys?.id
  );
}

/**
 * Decorative opening quote mark (Octicons path).
 * Inline SVG + currentColor so it always paints (CSS mask + external asset often fails silently).
 */
const QuoteMark = ({styles}) => (
  <Box as="span" aria-hidden="true" {...styles}>
    <svg viewBox="0 0 640 1024" xmlns="http://www.w3.org/2000/svg" focusable="false">
      <path
        fill="currentColor"
        d="M0 512v256h256V512H128c0 0 0-128 128-128V256C256 256 0 256 0 512zM640 384V256c0 0-256 0-256 256v256h256V512H512C512 512 512 384 640 384z"
      />
    </svg>
  </Box>
);

QuoteMark.propTypes = {
  styles: PropTypes.object,
};

/**
 * Featured In With Logos (CF-052) — heading + quote copy beside a row of award logos.
 *
 * Quote mark placement (Figma):
 * - Desktop: watermark behind the heading
 * - Mobile: watermark behind the quote body (brown border cuts through the mark)
 *
 * @param {Object} props - Component props
 * @param {Contentful.FeaturedInWithLogosCf052} props.component - Component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement|null}
 */
export const FeaturedInWithLogosCf052 = ({
  component,
  isComplete = true,
  isFetching = false,
  ...props
}) => {
  const styles = useMultiStyleConfig('contentful/featured-in-with-logos-cf052', {
    component,
    isComplete,
    isFetching,
  });

  const heading = component?.heading?.trim();
  const quote = component?.quote;
  const hasQuote = Boolean(quote?.json);
  const icons = filterValidItems(component?.iconsCollection?.items);
  const showQuoteMark = Boolean(heading || hasQuote);

  if (!isComplete && isFetching) {
    return (
      <Box className="component component--featured-in-with-logos-cf052" {...styles.root} {...props}>
        <Flex {...styles.container}>
          <Box {...styles.copyRegion}>
            <Skeleton height="28px" mb={4} width="60%" />
            <Skeleton height="72px" width="100%" />
          </Box>
          <Flex {...styles.logosRegion}>
            <Skeleton height="120px" width="120px" />
            <Skeleton height="120px" width="120px" />
          </Flex>
        </Flex>
      </Box>
    );
  }

  if (!heading && !hasQuote && icons.length === 0) {
    return null;
  }

  return (
    <Box className="component component--featured-in-with-logos-cf052" {...styles.root} {...props}>
      <Flex {...styles.container}>
        <Box {...styles.copyRegion}>
          {showQuoteMark ? <QuoteMark styles={styles.quoteMarkDesktop} /> : null}
          {heading ? (
            <Text as="h2" {...styles.heading}>
              {heading}
            </Text>
          ) : null}
          {hasQuote ? (
            <Box {...styles.quote}>
              {showQuoteMark ? <QuoteMark styles={styles.quoteMarkMobile} /> : null}
              <Box {...styles.quoteContent}>
                <RichTextContent content={quote} {...styles.quoteMarkup} />
              </Box>
            </Box>
          ) : null}
        </Box>
        {icons.length > 0 ? (
          <Flex {...styles.logosRegion} aria-label="Featured awards">
            {icons.map((item) => (
              <Box key={item.sys.id} {...styles.logoItem}>
                <ContentfulComponent
                  component={item}
                  isComplete={isComplete}
                  isFetching={isFetching}
                />
              </Box>
            ))}
          </Flex>
        ) : null}
      </Flex>
    </Box>
  );
};

FeaturedInWithLogosCf052.displayName = 'FeaturedInWithLogosCf052';
FeaturedInWithLogosCf052.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
